import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'fotografia-se-tatouaz-athina';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πώς μπορώ να μετατρέψω μια φωτογραφία σε τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Μέσω ενός έμπειρου καλλιτέχνη που θα μεταφράσει τη φωτογραφία σε τατουάζ σχέδιο. Δεν είναι απλή αντιγραφή, είναι καλλιτεχνική μετάφραση που λαμβάνει υπόψη τη συμπεριφορά του δέρματος και του μελανιού." } },
    { "@type": "Question", name: "Ποιες φωτογραφίες δουλεύουν καλύτερα ως τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Καθαρές, ευκρινείς φωτογραφίες με καλό φωτισμό, ξεκάθαρες σκιές, ένα κύριο θέμα και δυνατή σύνθεση. Για πορτρέτα, φωτογραφίες με καθαρά χαρακτηριστικά προσώπου." } },
    { "@type": "Question", name: "Ποιο είναι το ελάχιστο μέγεθος για ένα ρεαλιστικό πορτρέτο;", acceptedAnswer: { "@type": "Answer", text: "Συνήθως τουλάχιστον όσο το μπροστινό μέρος του βραχίονα ή του μηρού. Πολύ μικρότερα μεγέθη χάνουν τα χαρακτηριστικά και μπορεί να μη μοιάζουν με το πρόσωπο." } },
    { "@type": "Question", name: "Πόσο θα μοιάζει το τατουάζ με τη φωτογραφία;", acceptedAnswer: { "@type": "Answer", text: "Θα είναι μια καλλιτεχνική απόδοση, εμπνευσμένη από τη φωτογραφία. Ένας εξειδικευμένος καλλιτέχνης θα πετύχει μεγάλη ομοιότητα, αλλά ένα τατουάζ δεν είναι φωτογραφία εκτυπωμένη στο δέρμα." } },
    { "@type": "Question", name: "Πού μπορώ να μετατρέψω φωτογραφία σε τατουάζ στην Αθήνα;", acceptedAnswer: { "@type": "Answer", text: "Στο 210 Tattoo Athens υπάρχουν καλλιτέχνες με εμπειρία σε πορτρέτα, ρεαλισμό και άλλες μεταφράσεις φωτογραφίας. Στείλε την στο @210tattoo στο Instagram για αξιολόγηση." } },
  ],
};

export default function FotografiaSeTatouazAthina() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πώς να Μετατρέψεις μια Φωτογραφία σε Σχέδιο Τατουάζ",
      "description": "Οδηγός για την Αθήνα: πώς γίνεται η μετάφραση φωτογραφίας σε τατουάζ, ποιες φωτογραφίες δουλεύουν καλά, τα κατάλληλα στυλ και ρεαλιστικές προσδοκίες για πορτρέτα.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-07",
      "dateModified": "2026-07-07",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-photo-tattoo-faq', faqSchema);
    inject('schema-photo-tattoo-article', articleSchema);
    return () => {
      document.getElementById('schema-photo-tattoo-faq')?.remove();
      document.getElementById('schema-photo-tattoo-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Φωτογραφία σε Τατουάζ Αθήνα | 210 Tattoo Athens</title>
        <meta name="description" content="Πώς μετατρέπεται μια φωτογραφία σε σχέδιο τατουάζ στην Αθήνα: ποιες φωτογραφίες δουλεύουν, πώς εργάζεται ο καλλιτέχνης και ρεαλιστικές προσδοκίες για πορτρέτα." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Φωτογραφία σε Τατουάζ Αθήνα | 210 Tattoo Athens" />
        <meta property="og:description" content="Πώς γίνεται η καλλιτεχνική μετάφραση μιας φωτογραφίας σε τατουάζ και τι να προσέξεις." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Φωτογραφία σε Τατουάζ Αθήνα | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Ποιες φωτογραφίες δουλεύουν, τα κατάλληλα στυλ και ρεαλιστικές προσδοκίες." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Φωτογραφία σε Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Πώς να μετατρέψεις μια φωτογραφία</span>
              <br />
              <span className="text-white">σε σχέδιο τατουάζ</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">7 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Ένα καλό τατουάζ δεν είναι απλή αντιγραφή φωτογραφίας. Είναι μια δημιουργική μετάφραση, ένα ξεχωριστό έργο που χρησιμοποιεί τη φωτογραφία ως αφετηρία. Δες πώς γίνεται και πώς να πάρεις το καλύτερο αποτέλεσμα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Why not just copy */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί η φωτογραφία <span className="text-pink-500">δεν αντιγράφεται απλώς</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Μια φωτογραφία είναι ένα δισδιάστατο αρχείο με εκατομμύρια αποχρώσεις, μικροσκοπικές λεπτομέρειες, φως και σκιά. Το δέρμα σου δεν είναι οθόνη. Είναι ένα ζωντανό, τρισδιάστατο υλικό με τη δική του υφή και συμπεριφορά. Το μελάνι ξεθωριάζει με τον χρόνο, εξαπλώνεται ελαφρώς κάτω από το δέρμα και χάνει λεπτομέρειες σε μικρές κλίμακες.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Η φωτογραφία μπορεί να έχει στοιχεία που δεν λειτουργούν σε τατουάζ. Πολύ ανοιχτούς τόνους, φόντο που περισπά την προσοχή, λεπτομέρειες που θα χαθούν σε μικρότερο μέγεθος. Η μετάφραση περιλαμβάνει επιλογές: τι κρατάμε, τι αφαιρούμε, τι απλοποιούμε, τι τονίζουμε. Η σωστή επιλογή καλλιτέχνη είναι κρίσιμη εδώ, και ο οδηγός μας για το <Link to="/blog/pos-epilegeis-tattoo-artist-gia-stil-athina" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πώς επιλέγεις tattoo artist για το στυλ σου</Link> βοηθάει.
            </p>
          </div>
        </section>

        {/* Which photos work */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ποιες φωτογραφίες <span className="text-pink-500">δουλεύουν καλά ως τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν μεταφράζονται όλες οι φωτογραφίες το ίδιο καλά. Ορίστε τι κάνει μια καλή αφετηρία.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Καθαρή, ευκρινής εικόνα', desc: 'Καλή ανάλυση, καθαρή εστίαση και καλή έκθεση. Ο καλλιτέχνης χρειάζεται να βλέπει καθαρά τι υπάρχει στη φωτογραφία.' },
                { title: 'Καλός φωτισμός με ξεκάθαρες σκιές', desc: 'Ξεκάθαρες φωτεινές και σκοτεινές περιοχές δίνουν οδηγό για τις σκιάσεις. Επίπεδος φωτισμός δίνει πιο δύσκολο σχέδιο.' },
                { title: 'Ένα κύριο θέμα', desc: 'Απλό φόντο ή χωρίς αποσπάσεις. Πολλά αντικείμενα ή πολύπλοκο φόντο περιπλέκουν τη σύνθεση.' },
                { title: 'Δυνατή σύνθεση', desc: 'Στοιχεία τοποθετημένα με ισορροπία γίνονται και όμορφο τατουάζ. Μια πρόχειρη φωτογραφία θέλει περισσότερη δουλειά προσαρμογής.' },
                { title: 'Καθαρά χαρακτηριστικά', desc: 'Για πορτρέτα, ξεκάθαρα μάτια, μύτη και στόμα δίνουν καλύτερα αποτελέσματα. Το προφίλ ή το μερικώς κρυμμένο πρόσωπο είναι πιο απαιτητικό.' },
                { title: 'Πολλαπλές γωνίες', desc: 'Αν έχεις περισσότερες από μία φωτογραφίες, ο καλλιτέχνης μπορεί να χρησιμοποιήσει στοιχεία από όλες για το καλύτερο δυνατό.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς εργάζεται ο καλλιτέχνης <span className="text-pink-500">με τη φωτογραφία σου</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ας δούμε τη διαδικασία που ακολουθεί ένας έμπειρος καλλιτέχνης, χωρίς βιασύνη και με έμφαση στη σωστή προεργασία.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Αξιολόγηση της φωτογραφίας', desc: 'Εντοπίζει τα δυνατά και αδύναμα σημεία και σου λέει τι λειτουργεί και τι θα χρειαστεί προσαρμογή.' },
                { step: '02', title: 'Επιλογή στυλ', desc: 'Η ίδια φωτογραφία μεταφράζεται σε πολλά στυλ με διαφορετικό αποτέλεσμα, από ρεαλισμό μέχρι ακουαρέλα.' },
                { step: '03', title: 'Απόφαση για μέγεθος και σημείο', desc: 'Ένα ρεαλιστικό πορτρέτο χρειάζεται συγκεκριμένο ελάχιστο μέγεθος για να λειτουργήσει.' },
                { step: '04', title: 'Δημιουργία σκίτσου', desc: 'Ο καλλιτέχνης δεν αντιγράφει, μεταφράζει. Αποφασίζει ποιες λεπτομέρειες κρατάει και πώς θα δουλέψει τις σκιάσεις.' },
                { step: '05', title: 'Τελική προσαρμογή', desc: 'Με βάση τα σχόλιά σου προσαρμόζει το σκίτσο, όσες φορές χρειαστεί μέχρι να είσαι ικανοποιημένος.' },
                { step: '06', title: 'Εκτέλεση', desc: 'Μόνο όταν εσύ και ο καλλιτέχνης είστε σίγουροι για το σχέδιο, ξεκινάει η εκτέλεση.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-8">
              Αυτή η διαδικασία μοιάζει πολύ με το <Link to="/blog/custom-tattoo-process-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πώς γίνεται ένα custom τατουάζ, από την ιδέα στο σχέδιο</Link>.
            </p>
          </div>
        </section>

        {/* Styles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Στυλ που ταιριάζουν <span className="text-pink-500">σε φωτογραφίες</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Realism', desc: 'Ο πιο άμεσος τρόπος. Φαίνεται σαν φωτογραφία στο δέρμα. Ιδανικό για πορτρέτα ή ρεαλιστικά ζώα, θέλει έμπειρο καλλιτέχνη και μεγαλύτερο μέγεθος.' },
                { title: 'Black and Grey', desc: 'Ρεαλιστική δουλειά σε μαυρόγκρι τόνους. Κλασικό, δυναμικό αποτέλεσμα που γερνάει καλά. Ιδανικό για πορτρέτα και δραματικά κομμάτια.' },
                { title: 'Illustrative', desc: 'Πιο ελεύθερη ερμηνεία με σαφές καλλιτεχνικό στυλ. Η φωτογραφία γίνεται εικονογραφημένο έργο.' },
                { title: 'Neo-traditional', desc: 'Mix ρεαλισμού με παραδοσιακά στοιχεία, έντονες γραμμές και ζωντανά χρώματα. Ιδανικό για ζώα και λουλούδια.' },
                { title: 'Fine line ρεαλισμός', desc: 'Ντελικάτη προσέγγιση με λεπτές γραμμές και ήπιες σκιάσεις. Δεν αντέχει σε πολύ μικρά μεγέθη.' },
                { title: 'Watercolor', desc: 'Χρωματιστές, ρευστές πινελιές γύρω από το θέμα για ένα πιο αφηρημένο, καλλιτεχνικό αποτέλεσμα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Portraits and expectations */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ειδικά για πορτρέτα <span className="text-pink-500">και ρεαλιστικές προσδοκίες</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Τα πορτρέτα είναι από τις πιο δημοφιλείς αλλά και πιο απαιτητικές μεταφράσεις. Δεν κάνει κάθε καλλιτέχνης καλά πορτρέτα, είναι εξειδικευμένη δουλειά. Ζήτα να δεις παραδείγματα πορτρέτων, ιδανικά και σε επουλωμένη μορφή. Ένα καλό πορτρέτο έχει ζωντάνια, σωστές αναλογίες και συνολική συνέπεια, και χρειάζεται τουλάχιστον το μέγεθος του μπροστινού μέρους του βραχίονα ή του μηρού.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Ας είμαστε ρεαλιστές. Ένα τατουάζ πορτρέτο, όσο καλά κι αν είναι, δεν θα είναι ακριβώς σαν την φωτογραφία. Θα είναι μια καλλιτεχνική απόδοση, ένα σχέδιο εμπνευσμένο από το πρόσωπο. Πολλοί πελάτες φέρουν βαρύ συναισθηματικό φορτίο, ειδικά όταν πρόκειται για αγαπημένο πρόσωπο που δεν είναι πια εδώ, και η ομάδα του 210 Tattoo Athens αντιμετωπίζει τέτοιες περιπτώσεις με ευαισθησία και σεβασμό. Το ίδιο ισχύει για κατοικίδια: επίλεξε φωτογραφία που δείχνει την προσωπικότητα, ένα βλέμμα ή μια έκφραση που το χαρακτηρίζει.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έχεις μια φωτογραφία που θες <span className="text-pink-500">να μετατρέψεις σε τατουάζ;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε τη φωτογραφία σου και μια σύντομη περιγραφή του τι σημαίνει για σένα στο Instagram του στούντιο. Η ομάδα θα σου δώσει την αρχική της αξιολόγηση και θα προγραμματίσετε τη συνέχεια.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/custom-tattoo-process-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Γίνεται ένα Custom Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Από την ιδέα στο σχέδιο και την ημέρα της συνεδρίας.</p>
              </Link>
              <Link to="/blog/pos-epilegeis-tattoo-artist-gia-stil-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Διαλέγεις Tattoo Artist για το Στυλ σου</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς αναγνωρίζεις εξειδίκευση σε ρεαλισμό και πορτρέτα.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/color-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Color Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
